import { useCallback, useMemo, useState } from 'react'
import type { TechnicianLane } from './grid-helpers'

function readOrder(storageKey: string): string[] {
  try {
    const rawOrder = localStorage.getItem(storageKey)
    if (!rawOrder) return []

    const parsedOrder: unknown = JSON.parse(rawOrder)
    return Array.isArray(parsedOrder)
      ? parsedOrder.filter((id): id is string => typeof id === 'string')
      : []
  } catch {
    return []
  }
}

function persistOrder(storageKey: string, technicianIds: string[]): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(technicianIds))
  } catch {
    // Storage may be unavailable or full; keep the in-memory order usable.
  }
}

function orderLanes(
  lanes: TechnicianLane[],
  persistedIds: string[]
): TechnicianLane[] {
  const lanesById = new Map(lanes.map(lane => [lane.technicianId, lane]))
  const orderedIds = new Set<string>()
  const orderedLanes = persistedIds.flatMap(id => {
    if (orderedIds.has(id)) return []
    const lane = lanesById.get(id)
    if (lane) orderedIds.add(id)
    return lane ? [lane] : []
  })

  return [
    ...orderedLanes,
    ...lanes.filter(lane => !orderedIds.has(lane.technicianId)),
  ]
}

function mergeVisibleOrder(
  persistedIds: string[],
  visibleIds: string[]
): string[] {
  const visibleSet = new Set(visibleIds)
  const nextOrder: string[] = []
  let insertedVisible = false

  for (const id of persistedIds) {
    if (!visibleSet.has(id)) {
      nextOrder.push(id)
      continue
    }

    if (!insertedVisible) {
      nextOrder.push(...visibleIds)
      insertedVisible = true
    }
  }

  if (!insertedVisible) nextOrder.push(...visibleIds)
  return Array.from(new Set(nextOrder))
}

export function useTechnicianColumnOrder(
  storageKey: string,
  lanes: TechnicianLane[]
): {
  orderedLanes: TechnicianLane[]
  moveLane: (technicianId: string, targetTechnicianId: string) => void
  resetOrder: () => void
} {
  const [ordersByStorageKey, setOrdersByStorageKey] = useState<
    Record<string, string[]>
  >(() => ({ [storageKey]: readOrder(storageKey) }))
  const persistedIds = ordersByStorageKey[storageKey] ?? readOrder(storageKey)
  const orderedLanes = useMemo(
    () => orderLanes(lanes, persistedIds),
    [lanes, persistedIds]
  )

  const moveLane = useCallback(
    (technicianId: string, targetTechnicianId: string) => {
      if (technicianId === targetTechnicianId) return

      const currentOrder = orderedLanes.map(lane => lane.technicianId)
      const sourceIndex = currentOrder.indexOf(technicianId)
      const targetIndex = currentOrder.indexOf(targetTechnicianId)
      if (sourceIndex === -1 || targetIndex === -1) return

      const shouldInsertAfterTarget = sourceIndex < targetIndex
      currentOrder.splice(sourceIndex, 1)
      const targetIndexAfterRemoval = currentOrder.indexOf(targetTechnicianId)
      const insertionIndex = shouldInsertAfterTarget
        ? targetIndexAfterRemoval + 1
        : targetIndexAfterRemoval
      currentOrder.splice(insertionIndex, 0, technicianId)
      const nextOrder = mergeVisibleOrder(persistedIds, currentOrder)
      persistOrder(storageKey, nextOrder)
      setOrdersByStorageKey(currentOrders => ({
        ...currentOrders,
        [storageKey]: nextOrder,
      }))
    },
    [orderedLanes, persistedIds, storageKey]
  )

  const resetOrder = useCallback(() => {
    const nextIds = lanes.map(lane => lane.technicianId)
    const nextOrder = mergeVisibleOrder(persistedIds, nextIds)
    persistOrder(storageKey, nextOrder)
    setOrdersByStorageKey(currentOrders => ({
      ...currentOrders,
      [storageKey]: nextOrder,
    }))
  }, [lanes, persistedIds, storageKey])

  return { orderedLanes, moveLane, resetOrder }
}
